import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_BASE_URL } from '../../utils/Controller/Controller';
import { BlogShareButton } from '../UI/BlogShareButtons';
import { AppPrimaryButton, AppOutlineButton } from '../UI/CustomButtons';
import { JOB_PATH, REGISTRATION_PATH, JOBS_DASHBOARD_PATH } from '../../utils/Constants/Paths';
import useTheme from '../../hooks/useTheme';
import { StickyCTA } from '../UI/StickyCTA';
import { logUserAction } from '../../utils/analyticsFunctions';
import UserActions from '../../utils/Constants/UserActions';

const SalaryCalculator = ({ inputClass = '', labelClass = '', sliderClass = '', sliderContainerClass = '', sliderValueClass = '', resultClass = '' }) => {
  const theme = useTheme();
  const location = useLocation()

  useEffect(() => {
    logUserAction(UserActions.salary_calculator_viewed);
  }, []);

  // Multi-step flow state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // State variables with proper initialization
  const [hourlyRate, setHourlyRate] = useState(0);
  const [workWeeks, setWorkWeeks] = useState(0);
  const [workHours, setWorkHours] = useState(0);
  const [otherCompanyCosts, setOtherCompanyCosts] = useState(0);
  const [desiredGrossSalary, setDesiredGrossSalary] = useState(0);
  const [netSalary, setNetSalary] = useState(0);
  const [dividend, setDividend] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [municipalityTaxRate, setMunicipalityTaxRate] = useState(33);
  const [maxGrossSalary, setMaxGrossSalary] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // Gamified animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousNetTotal, setPreviousNetTotal] = useState(0);

  // Warning state for when costs are too high
  const [showCostWarning, setShowCostWarning] = useState(false);

  // Lägg till state för skatteuppdelning
  const [municipalTaxAmount, setMunicipalTaxAmount] = useState(0);
  const [stateTaxAmount, setStateTaxAmount] = useState(0);
  // Lägg till state för total inkomstskatt (kommunal + statlig)
  const [totalIncomeTaxRate, setTotalIncomeTaxRate] = useState(municipalityTaxRate);
  // Lägg till state för arbetsgivaravgift
  const [employerFeeAmount, setEmployerFeeAmount] = useState(0);
  // Lägg till state för utdelningsskatt
  const [dividendTaxAmount, setDividendTaxAmount] = useState(0);

  // Responsive state management to prevent resets on window resize
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const MUNICIPALITIES_DATA_UNSORTED = [
    { label: 'Ale kommun', value: 'ale', rate: 33.13 },
    { label: 'Alingsås', value: 'Alingsås', rate: 33.24 },
    { label: 'Alvesta', value: 'Alvesta', rate: 33.43 },
    { label: 'Aneby', value: 'Aneby', rate: 34.54 },
    { label: 'Arboga', value: 'Arboga', rate: 33.38 },
    { label: 'Arjeplog', value: 'Arjeplog', rate: 33.78 },
    { label: 'Arvidsjaur', value: 'Arvidsjaur', rate: 33.92 },
    { label: 'Arvika', value: 'Arvika', rate: 33.67 },
    { label: 'Askersund', value: 'Askersund', rate: 33.48 },
    { label: 'Avesta', value: 'Avesta', rate: 34.61 },
    { label: 'Bengtsfors', value: 'Bengtsfors', rate: 34.45 },
    { label: 'Berg', value: 'Berg', rate: 33.82 },
    { label: 'Bjuv', value: 'Bjuv', rate: 33.85 },
    { label: 'Boden', value: 'Boden', rate: 33.8 },
    { label: 'Bollebygd', value: 'Bollebygd', rate: 32.34 },
    { label: 'Bollnäs', value: 'Bollnäs', rate: 34.05 },
    { label: 'Borgholm', value: 'Borgholm', rate: 33.3 },
    { label: 'Borlänge', value: 'Borlänge', rate: 33.6 },
    { label: 'Borås', value: 'Borås', rate: 33.14 },
    { label: 'Boxholm', value: 'Boxholm', rate: 33.42 },
    { label: 'Bromölla', value: 'Bromölla', rate: 33.02 },
    { label: 'Dals-Ed', value: 'Dals-Ed', rate: 34.02 },
    { label: 'Danderyd', value: 'Danderyd', rate: 29.82 },
    { label: 'Degerfors', value: 'Degerfors', rate: 33.99 },
    { label: 'Dorotea', value: 'Dorotea', rate: 34.49 },
    { label: 'Eda', value: 'Eda', rate: 33.8 },
    { label: 'Ekerö', value: 'Ekerö', rate: 30.82 },
    { label: 'Eksjö', value: 'Eksjö', rate: 33.45 },
    { label: 'Enköping', value: 'Enköping', rate: 32.88 },
    { label: 'Eskilstuna', value: 'Eskilstuna', rate: 33.68 },
    { label: 'Eslöv', value: 'Eslöv', rate: 33.57 },
    { label: 'Essunga', value: 'Essunga', rate: 33.48 },
    { label: 'Fagersta', value: 'Fagersta', rate: 34.05 },
    { label: 'Falkenberg', value: 'Falkenberg', rate: 32.88 },
    { label: 'Falköping', value: 'Falköping', rate: 33.6 },
    { label: 'Falun', value: 'Falun', rate: 33.3 },
    { label: 'Filipstad', value: 'Filipstad', rate: 34.7 },
    { label: 'Finspång', value: 'Finspång', rate: 33.7 },
    { label: 'Flen', value: 'Flen', rate: 33.48 },
    { label: 'Forshaga', value: 'Forshaga', rate: 33.5 },
    { label: 'Gagnef', value: 'Gagnef', rate: 33.38 },
    { label: 'Gislaved', value: 'Gislaved', rate: 33.35 },
    { label: 'Gnesta', value: 'Gnesta', rate: 32.95 },
    { label: 'Gnosjö', value: 'Gnosjö', rate: 33.08 },
    { label: 'Gotland', value: 'Gotland', rate: 32.32 },
    { label: 'Grums', value: 'Grums', rate: 33.7 },
    { label: 'Grästorp', value: 'Grästorp', rate: 33.87 },
    { label: 'Gullspång', value: 'Gullspång', rate: 33.8 },
    { label: 'Gällivare', value: 'Gällivare', rate: 34.45 },
    { label: 'Gävle', value: 'Gävle', rate: 33.38 },
    { label: 'Göteborg', value: 'Göteborg', rate: 33.38 },
    { label: 'Habo', value: 'Habo', rate: 32.6 },
    { label: 'Hagfors', value: 'Hagfors', rate: 34.04 },
    { label: 'Hallsberg', value: 'Hallsberg', rate: 33.58 },
    { label: 'Hallstahammar', value: 'Hallstahammar', rate: 33.78 },
    { label: 'Halmstad', value: 'Halmstad', rate: 32.67 },
    { label: 'Helsingborg', value: 'Helsingborg', rate: 32.42 },
    { label: 'Hjo', value: 'Hjo', rate: 33.2 },
    { label: 'Hofors', value: 'Hofors', rate: 34.7 },
    { label: 'Huddinge', value: 'Huddinge', rate: 31.72 },
    { label: 'Hudiksvall', value: 'Hudiksvall', rate: 33.8 },
    { label: 'Hultsfred', value: 'Hultsfred', rate: 33.58 },
    { label: 'Hylte', value: 'Hylte', rate: 34.1 },
    { label: 'Håbo', value: 'Håbo', rate: 32.75 },
    { label: 'Härjedalen', value: 'Härjedalen', rate: 33.77 },
    { label: 'Härnösand', value: 'Härnösand', rate: 33.28 },
    { label: 'Hässleholm', value: 'Hässleholm', rate: 33.05 },
    { label: 'Höganäs', value: 'Höganäs', rate: 31.82 },
    { label: 'Högsby', value: 'Högsby', rate: 33.5 },
    { label: 'Hörby', value: 'Hörby', rate: 33.8 },
    { label: 'Höör', value: 'Höör', rate: 33.05 },
    { label: 'Jokkmokk', value: 'Jokkmokk', rate: 34.49 },
    { label: 'Järfälla', value: 'Järfälla', rate: 31.3 },
    { label: 'Jönköping', value: 'Jönköping', rate: 32.6 },
    { label: 'Kalix', value: 'Kalix', rate: 34.14 },
    { label: 'Kalmar', value: 'Kalmar', rate: 32.4 },
    { label: 'Karlsborg', value: 'Karlsborg', rate: 33.82 },
    { label: 'Karlshamn', value: 'Karlshamn', rate: 33.33 },
    { label: 'Karlskoga', value: 'Karlskoga', rate: 33.5 },
    { label: 'Karlskrona', value: 'Karlskrona', rate: 32.55 },
    { label: 'Karlstad', value: 'Karlstad', rate: 32.7 },
    { label: 'Katrineholm', value: 'Katrineholm', rate: 33.29 },
    { label: 'Kil', value: 'Kil', rate: 33.6 },
    { label: 'Kinda', value: 'Kinda', rate: 33.8 },
    { label: 'Kiruna', value: 'Kiruna', rate: 33.08 },
    { label: 'Klippan', value: 'Klippan', rate: 33.82 },
    { label: 'Kramfors', value: 'Kramfors', rate: 34.05 },
    { label: 'Kristianstad', value: 'Kristianstad', rate: 32.77 },
    { label: 'Kristinehamn', value: 'Kristinehamn', rate: 33.7 },
    { label: 'Krokom', value: 'Krokom', rate: 33.48 },
    { label: 'Kumla', value: 'Kumla', rate: 33.08 },
    { label: 'Kungsbacka', value: 'Kungsbacka', rate: 31.64 },
    { label: 'Kungsör', value: 'Kungsör', rate: 33.8 },
    { label: 'Kungälv', value: 'Kungälv', rate: 33.47 },
    { label: 'Laholm', value: 'Laholm', rate: 33.18 },
    { label: 'Landskrona', value: 'Landskrona', rate: 32.88 },
    { label: 'Laxå', value: 'Laxå', rate: 34.2 },
    { label: 'Lekeberg', value: 'Lekeberg', rate: 33.2 },
    { label: 'Leksand', value: 'Leksand', rate: 33.42 },
    { label: 'Lerum', value: 'Lerum', rate: 32.54 },
    { label: 'Lidingö', value: 'Lidingö', rate: 30.5 },
    { label: 'Lidköping', value: 'Lidköping', rate: 33.18 },
    { label: 'Lilla Edet', value: 'Lilla Edet', rate: 33.38 },
    { label: 'Lindesberg', value: 'Lindesberg', rate: 33.68 },
    { label: 'Linköping', value: 'Linköping', rate: 32.62 },
    { label: 'Ljungby', value: 'Ljungby', rate: 32.72 },
    { label: 'Ljusdal', value: 'Ljusdal', rate: 34.2 },
    { label: 'Ljusnarsberg', value: 'Ljusnarsberg', rate: 34.7 },
    { label: 'Lomma', value: 'Lomma', rate: 31.12 },
    { label: 'Ludvika', value: 'Ludvika', rate: 33.88 },
    { label: 'Luleå', value: 'Luleå', rate: 33.35 },
    { label: 'Lund', value: 'Lund', rate: 31.53 },
    { label: 'Lycksele', value: 'Lycksele', rate: 34.2 },
    { label: 'Lysekil', value: 'Lysekil', rate: 33.5 },
    { label: 'Malmö', value: 'Malmö', rate: 32.42 },
    { label: 'Malung-Sälen', value: 'Malung-Sälen', rate: 33.95 },
    { label: 'Malå', value: 'Malå', rate: 34.92 },
    { label: 'Mariestad', value: 'Mariestad', rate: 33.07 },
    { label: 'Mark', value: 'Mark', rate: 33.43 },
    { label: 'Markaryd', value: 'Markaryd', rate: 33.85 },
    { label: 'Mellerud', value: 'Mellerud', rate: 33.63 },
    { label: 'Mjölby', value: 'Mjölby', rate: 33.28 },
    { label: 'Mora', value: 'Mora', rate: 33.25 },
    { label: 'Motala', value: 'Motala', rate: 33.38 },
    { label: 'Mullsjö', value: 'Mullsjö', rate: 33.25 },
    { label: 'Nacka', value: 'Nacka', rate: 31.5 },
    { label: 'Nora', value: 'Nora', rate: 33.88 },
    { label: 'Norberg', value: 'Norberg', rate: 34.1 },
    { label: 'Nordanstig', value: 'Nordanstig', rate: 34.55 },
    { label: 'Nordmaling', value: 'Nordmaling', rate: 33.65 },
    { label: 'Norrköping', value: 'Norrköping', rate: 32.65 },
    { label: 'Norrtälje', value: 'Norrtälje', rate: 32.98 },
    { label: 'Norsjö', value: 'Norsjö', rate: 34.75 },
    { label: 'Nybro', value: 'Nybro', rate: 33.28 },
    { label: 'Nykvarn', value: 'Nykvarn', rate: 32.18 },
    { label: 'Nyköping', value: 'Nyköping', rate: 33.03 },
    { label: 'Nynäshamn', value: 'Nynäshamn', rate: 32.88 },
    { label: 'Nässjö', value: 'Nässjö', rate: 33.35 },
    { label: 'Ockelbo', value: 'Ockelbo', rate: 34.08 },
    { label: 'Olofström', value: 'Olofström', rate: 33.83 },
    { label: 'Orsa', value: 'Orsa', rate: 33.63 },
    { label: 'Orust', value: 'Orust', rate: 33.35 },
    { label: 'Osby', value: 'Osby', rate: 33.65 },
    { label: 'Oskarshamn', value: 'Oskarshamn', rate: 33.28 },
    { label: 'Ovanåker', value: 'Ovanåker', rate: 33.98 },
    { label: 'Oxelösund', value: 'Oxelösund', rate: 33.28 },
    { label: 'Pajala', value: 'Pajala', rate: 34.77 },
    { label: 'Partille', value: 'Partille', rate: 32.45 },
    { label: 'Perstorp', value: 'Perstorp', rate: 33.88 },
    { label: 'Piteå', value: 'Piteå', rate: 33.65 },
    { label: 'Ragunda', value: 'Ragunda', rate: 34.15 },
    { label: 'Robertsfors', value: 'Robertsfors', rate: 34.2 },
    { label: 'Ronneby', value: 'Ronneby', rate: 33.38 },
    { label: 'Rättvik', value: 'Rättvik', rate: 33.18 },
    { label: 'Sala', value: 'Sala', rate: 33.38 },
    { label: 'Salem', value: 'Salem', rate: 32.18 },
    { label: 'Sandviken', value: 'Sandviken', rate: 33.9 },
    { label: 'Sigtuna', value: 'Sigtuna', rate: 32.38 },
    { label: 'Simrishamn', value: 'Simrishamn', rate: 33.1 },
    { label: 'Sjöbo', value: 'Sjöbo', rate: 33.6 },
    { label: 'Skara', value: 'Skara', rate: 33.18 },
    { label: 'Skellefteå', value: 'Skellefteå', rate: 33.43 },
    { label: 'Skinnskatteberg', value: 'Skinnskatteberg', rate: 34.2 },
    { label: 'Skurup', value: 'Skurup', rate: 32.9 },
    { label: 'Smedjebacken', value: 'Smedjebacken', rate: 33.88 },
    { label: 'Sollefteå', value: 'Sollefteå', rate: 34.3 },
    { label: 'Sollentuna', value: 'Sollentuna', rate: 31.47 },
    { label: 'Solna', value: 'Solna', rate: 29.82 },
    { label: 'Sorsele', value: 'Sorsele', rate: 34.7 },
    { label: 'Sotenäs', value: 'Sotenäs', rate: 33.2 },
    { label: 'Staffanstorp', value: 'Staffanstorp', rate: 32.42 },
    { label: 'Stenungsund', value: 'Stenungsund', rate: 33.38 },
    { label: 'Stockholm', value: 'Stockholm', rate: 29.82 },
    { label: 'Storfors', value: 'Storfors', rate: 34.7 },
    { label: 'Storuman', value: 'Storuman', rate: 34.65 },
    { label: 'Strängnäs', value: 'Strängnäs', rate: 32.9 },
    { label: 'Strömstad', value: 'Strömstad', rate: 33.35 },
    { label: 'Sundbyberg', value: 'Sundbyberg', rate: 30.75 },
    { label: 'Sundsvall', value: 'Sundsvall', rate: 33.08 },
    { label: 'Sunne', value: 'Sunne', rate: 33.8 },
    { label: 'Surahammar', value: 'Surahammar', rate: 33.68 },
    { label: 'Svalöv', value: 'Svalöv', rate: 33.88 },
    { label: 'Svedala', value: 'Svedala', rate: 32.77 },
    { label: 'Svenljunga', value: 'Svenljunga', rate: 33.54 },
    { label: 'Säffle', value: 'Säffle', rate: 33.67 },
    { label: 'Säter', value: 'Säter', rate: 33.5 },
    { label: 'Sävsjö', value: 'Sävsjö', rate: 33.48 },
    { label: 'Söderhamn', value: 'Söderhamn', rate: 34.35 },
    { label: 'Söderköping', value: 'Söderköping', rate: 32.75 },
    { label: 'Södertälje', value: 'Södertälje', rate: 32.42 },
    { label: 'Tidaholm', value: 'Tidaholm', rate: 33.4 },
    { label: 'Tierp', value: 'Tierp', rate: 33.48 },
    { label: 'Timrå', value: 'Timrå', rate: 33.78 },
    { label: 'Tingsryd', value: 'Tingsryd', rate: 33.85 },
    { label: 'Tranemo', value: 'Tranemo', rate: 33.34 },
    { label: 'Tranås', value: 'Tranås', rate: 33.03 },
    { label: 'Trelleborg', value: 'Trelleborg', rate: 33.18 },
    { label: 'Trosa', value: 'Trosa', rate: 32.2 },
    { label: 'Tyresö', value: 'Tyresö', rate: 31.6 },
    { label: 'Täby', value: 'Täby', rate: 30.5 },
    { label: 'Töreboda', value: 'Töreboda', rate: 33.7 },
    { label: 'Uddevalla', value: 'Uddevalla', rate: 33.48 },
    { label: 'Ulricehamn', value: 'Ulricehamn', rate: 33.54 },
    { label: 'Umeå', value: 'Umeå', rate: 32.53 },
    { label: 'Upplands Väsby', value: 'Upplands Väsby', rate: 32.15 },
    { label: 'Upplands-Bro', value: 'Upplands-Bro', rate: 32.4 },
    { label: 'Uppsala', value: 'Uppsala', rate: 32.63 },
    { label: 'Vaggeryd', value: 'Vaggeryd', rate: 33.2 },
    { label: 'Valdemarsvik', value: 'Valdemarsvik', rate: 33.65 },
    { label: 'Vallentuna', value: 'Vallentuna', rate: 31.8 },
    { label: 'Vansbro', value: 'Vansbro', rate: 33.58 },
    { label: 'Vara', value: 'Vara', rate: 33.6 },
    { label: 'Varberg', value: 'Varberg', rate: 32.88 },
    { label: 'Vellinge', value: 'Vellinge', rate: 29.19 },
    { label: 'Vetlanda', value: 'Vetlanda', rate: 33.35 },
    { label: 'Vilhelmina', value: 'Vilhelmina', rate: 34.2 },
    { label: 'Vimmerby', value: 'Vimmerby', rate: 33.3 },
    { label: 'Vindeln', value: 'Vindeln', rate: 34.35 },
    { label: 'Vingåker', value: 'Vingåker', rate: 33.7 },
    { label: 'Vårgårda', value: 'Vårgårda', rate: 33.48 },
    { label: 'Vänersborg', value: 'Vänersborg', rate: 33.57 },
    { label: 'Värnamo', value: 'Värnamo', rate: 33.1 },
    { label: 'Västervik', value: 'Västervik', rate: 33.18 },
    { label: 'Västerås', value: 'Västerås', rate: 32.34 },
    { label: 'Växjö', value: 'Växjö', rate: 32.43 },
    { label: 'Ydre', value: 'Ydre', rate: 34.52 },
    { label: 'Ystad', value: 'Ystad', rate: 32.72 },
    { label: 'Åmål', value: 'Åmål', rate: 33.88 },
    { label: 'Ånge', value: 'Ånge', rate: 34.55 },
    { label: 'Åre', value: 'Åre', rate: 33.38 },
    { label: 'Årjäng', value: 'Årjäng', rate: 33.82 },
    { label: 'Åsele', value: 'Åsele', rate: 34.7 },
    { label: 'Åstorp', value: 'Åstorp', rate: 33.55 },
    { label: 'Älmhult', value: 'Älmhult', rate: 33.32 },
    { label: 'Älvdalen', value: 'Älvdalen', rate: 33.45 },
    { label: 'Älvkarleby', value: 'Älvkarleby', rate: 33.48 },
    { label: 'Ängelholm', value: 'Ängelholm', rate: 32.02 },
    { label: 'Öckerö', value: 'Öckerö', rate: 32.78 },
    { label: 'Ödeshög', value: 'Ödeshög', rate: 33.58 },
    { label: 'Örebro', value: 'Örebro', rate: 32.65 },
    { label: 'Örkelljunga', value: 'Örkelljunga', rate: 33.65 },
    { label: 'Örnsköldsvik', value: 'Örnsköldsvik', rate: 33.08 },
    { label: 'Östersund', value: 'Östersund', rate: 33.28 },
    { label: 'Österåker', value: 'Österåker', rate: 32.1 },
    { label: 'Östhammar', value: 'Östhammar', rate: 33.48 }
  ];

  const primaryColor = '#ff5a5a';
  const complementaryColor = '#5affff';

  // Helper function to scroll to step header
  const scrollToStepHeader = (stepNumber = currentStep) => {
    // Try multiple selectors to ensure we find the right element
    const stepHeader = document.querySelector('.apple-calc-card-header');
    const calculatorCard = document.querySelector('.apple-calc-card');
    const calculatorContainer = document.querySelector('.apple-calc-container');

    // Prefer step header, fallback to card, then container
    const targetElement = stepHeader || calculatorCard || calculatorContainer;

    if (targetElement) {
      // For mobile devices, use a slightly different scroll behavior for better UX
      const isMobileDevice = window.innerWidth <= 768;

      // For step 4, we want to ensure users see the full instructions AND the revenue section
      // so we scroll much higher to show the step title, description, and revenue display
      let offsetAdjustment = isMobileDevice ? 40 : 60;

      // Extra offset for step 4 to ensure full visibility of instructions and revenue section
      if (stepNumber === 4) {
        offsetAdjustment = isMobileDevice ? 120 : 160;
      }

      // Use window.scrollTo for better control and consistency
      if (targetElement.offsetTop !== undefined) {
        window.scrollTo({
          top: Math.max(0, targetElement.offsetTop - offsetAdjustment),
          behavior: 'smooth'
        });
      } else {
        // Fallback to scrollIntoView if offsetTop is not available
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  };

  // Step navigation functions
  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => {
        const newStep = Math.min(prev + 1, totalSteps);
        // For step 4, use a longer delay to ensure DOM is fully updated
        const delay = newStep === 4 ? 300 : 200;
        setTimeout(() => {
          scrollToStepHeader(newStep);
        }, delay);
        return newStep;
      });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => {
      const newStep = Math.max(prev - 1, 1);
      // Use a longer delay to ensure DOM has updated with new step content
      setTimeout(() => {
        scrollToStepHeader(newStep);
      }, 200);
      return newStep;
    });
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    // For step 4, use a longer delay to ensure DOM is fully updated
    const delay = step === 4 ? 300 : 200;
    setTimeout(() => {
      scrollToStepHeader(step);
    }, delay);
  };

  // Validation for each step
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1: // Your Rate
        return hourlyRate > 0;
      case 2: // Work Schedule
        return workWeeks > 0 && workHours > 0;
      case 3: // Location & Costs
        return selectedMunicipality !== '';
      case 4: // Results
        return true; // Always valid
      default:
        return false;
    }
  };

  // Check if step is completed
  const isStepCompleted = (step) => {
    switch (step) {
      case 1:
        return hourlyRate > 0;
      case 2:
        return workWeeks > 0 && workHours > 0;
      case 3:
        return selectedMunicipality !== '';
      case 4:
        return desiredGrossSalary > 0;
      default:
        return false;
    }
  };

  // Get step titles
  const getStepTitle = (step) => {
    switch (step) {
      case 1:
        return 'Din timtaxa';
      case 2:
        return 'Arbetstid';
      case 3:
        return 'Plats & kostnader';
      case 4:
        return 'Lön & resultat';
      default:
        return '';
    }
  };

  // Get step description
  const getStepDescription = (step) => {
    switch (step) {
      case 1:
        return 'Ange din fakturering per timme';
      case 2:
        return 'Hur mycket tänker du arbeta?';
      case 3:
        return 'Var bor du och vilka kostnader har företaget?';
      case 4:
        return 'Bestäm din önskade lön och se resultatet';
      default:
        return '';
    }
  };

  // Calculate max gross salary whenever inputs change
  useEffect(() => {
    if (hourlyRate > 0 && workWeeks > 0 && workHours > 0 && otherCompanyCosts >= 0) {
      const totalAnnualRevenue = hourlyRate * workWeeks * workHours;
      const employerFeeRate = 0.3142;
      // Maximal bruttolön = (omsättning - kostnader) / (1 + arbetsgivaravgift)
      const annualProfitBeforeTax = totalAnnualRevenue - otherCompanyCosts;
      const maxAnnualGross = Math.max(0, annualProfitBeforeTax / (1 + employerFeeRate));
      const monthlyMaxGross = maxAnnualGross / 12;
      setMaxGrossSalary(monthlyMaxGross);
      // Justera bruttolön om den överskrider max
      if (desiredGrossSalary > monthlyMaxGross && monthlyMaxGross > 0) {
        setDesiredGrossSalary(monthlyMaxGross);
      }
    } else {
      setMaxGrossSalary(0);
      // Only reset desiredGrossSalary if it was previously set and we now have invalid inputs
      if (desiredGrossSalary > 0 && (hourlyRate === 0 || workWeeks === 0 || workHours === 0)) {
        setDesiredGrossSalary(0);
      }
    }
  }, [hourlyRate, workWeeks, workHours, otherCompanyCosts]);

  // Beräkna lön och utdelning automatiskt vid ändring av inputs
  useEffect(() => {
    // Samma logik som i calculateSalary, men utan alerts
    if (
      hourlyRate > 0 &&
      workWeeks > 0 &&
      workHours > 0 &&
      otherCompanyCosts >= 0 &&
      municipalityTaxRate >= 0 &&
      municipalityTaxRate <= 100
    ) {
      const totalAnnualRevenue = hourlyRate * workWeeks * workHours;
      const annualDesiredGrossSalary = desiredGrossSalary * 12;
      const employerFeeRate = 0.3142; // 31,42% arbetsgivaravgift
      const employerFee = annualDesiredGrossSalary * employerFeeRate;

      // Allmän vinst före skatt och avgifter
      const annualProfitBeforeAll = totalAnnualRevenue - otherCompanyCosts;

      // Kontrollera om det finns tillräckligt med intäkter för att täcka lön + arbetsgivaravgift
      const totalLaborCost = annualDesiredGrossSalary + employerFee;

      if (annualProfitBeforeAll < totalLaborCost && annualDesiredGrossSalary > 0) {
        // Inte tillräckligt med pengar för att täcka lön + arbetsgivaravgift
        setShowCostWarning(true);

        // Beräkna ändå löneskatten för att visa nettolönen
        const stateTaxThreshold = 598500; // Justerad brytpunkt för 2025
        const stateTaxRate = 0.20;
        const municipalTax = annualDesiredGrossSalary * (municipalityTaxRate / 100);
        const stateTax = annualDesiredGrossSalary > stateTaxThreshold
          ? (annualDesiredGrossSalary - stateTaxThreshold) * stateTaxRate
          : 0;
        const totalTax = municipalTax + stateTax;
        const netSalaryAfterTax = annualDesiredGrossSalary - totalTax;

        // Sätt lönerelaterade värden men ingen utdelning (eller negativ utdelning)
        setNetSalary(Math.round(netSalaryAfterTax));
        setDividend(0); // Ingen utdelning när kostnader är för höga
        setMunicipalTaxAmount(Math.round(municipalTax));
        setStateTaxAmount(Math.round(stateTax));
        setEmployerFeeAmount(Math.round(employerFee));
        setDividendTaxAmount(0);
      } else {
        // Vanlig beräkning när det finns tillräckligt med intäkter
        setShowCostWarning(false);
        // Dra lön + arbetsgivaravgift för utdelningsutrymme
        const annualProfitBeforeTax = annualProfitBeforeAll - annualDesiredGrossSalary - employerFee;
        const corporateTaxRate = 0.206; // 20,6% bolagsskatt för 2025
        const profitAfterTax = Math.max(0, annualProfitBeforeTax * (1 - corporateTaxRate));

        // Skatt på lön - Uppdaterade brytpunkter för 2025
        const stateTaxThreshold = 598500; // Justerad brytpunkt för 2025
        const stateTaxRate = 0.20;
        const municipalTax = annualDesiredGrossSalary * (municipalityTaxRate / 100);
        const stateTax = annualDesiredGrossSalary > stateTaxThreshold
          ? (annualDesiredGrossSalary - stateTaxThreshold) * stateTaxRate
          : 0;
        const totalTax = municipalTax + stateTax;
        const netSalaryAfterTax = annualDesiredGrossSalary - totalTax;

        // Utdelning - Uppdaterade regler för 2025
        let dividendPortion = profitAfterTax;
        if (dividendPortion < 0) dividendPortion = 0;

        // 3:12-reglerna för utdelning 2025
        const dividendFreeAmount = 209550; // Gränsbelopp för lägre beskattning 2025
        const taxRateBelowLimit = 0.20; // 20% under gränsbeloppet
        const taxRateAboveLimit = 0.30; // 30% över gränsbeloppet

        let dividendTax = 0;
        if (dividendPortion <= dividendFreeAmount) {
          dividendTax = dividendPortion * taxRateBelowLimit;
        } else {
          const taxableAmountBelowLimit = dividendFreeAmount;
          const taxableAmountAboveLimit = dividendPortion - dividendFreeAmount;
          const taxBelowLimit = taxableAmountBelowLimit * taxRateBelowLimit;
          const taxAboveLimit = taxableAmountAboveLimit * taxRateAboveLimit;
          dividendTax = taxBelowLimit + taxAboveLimit;
        }
        const dividendAfterTax = dividendPortion - dividendTax;
        setNetSalary(Math.round(netSalaryAfterTax));
        setDividend(Math.round(dividendAfterTax));
        setMunicipalTaxAmount(Math.round(municipalTax));
        setStateTaxAmount(Math.round(stateTax));
        setEmployerFeeAmount(Math.round(employerFee));
        setDividendTaxAmount(Math.round(dividendTax));
      }
    } else {
      setNetSalary(0);
      setDividend(null);
      setMunicipalTaxAmount(0);
      setStateTaxAmount(0);
      setEmployerFeeAmount(0);
      setDividendTaxAmount(0);
      setShowCostWarning(false);
    }
  }, [hourlyRate, workWeeks, workHours, otherCompanyCosts, desiredGrossSalary, municipalityTaxRate]);

  // Beräkna total inkomstskatt i procent
  useEffect(() => {
    const annualDesiredGrossSalary = desiredGrossSalary * 12;
    const stateTaxThreshold = 598500; // Uppdaterad brytpunkt för 2025
    const stateTaxRate = 0.20;
    let stateTaxPercent = 0;
    if (annualDesiredGrossSalary > stateTaxThreshold) {
      // Statlig skatt på överskjutande del
      const stateTaxAmount = (annualDesiredGrossSalary - stateTaxThreshold) * stateTaxRate;
      stateTaxPercent = (stateTaxAmount / annualDesiredGrossSalary) * 100;
    }
    setTotalIncomeTaxRate(municipalityTaxRate + stateTaxPercent);
  }, [hourlyRate, workWeeks, workHours, otherCompanyCosts, desiredGrossSalary, municipalityTaxRate]);

  // Handle responsive breakpoints with proper state management
  useEffect(() => {
    const updateResponsiveState = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      setIsDesktop(width > 1024);
    };

    // Set initial values
    updateResponsiveState();

    // Add resize listener
    window.addEventListener('resize', updateResponsiveState);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateResponsiveState);
    };
  }, []);

  // Trigger animation when net total changes (for gamification)
  useEffect(() => {
    if (currentStep === 4 && showResult) {
      const currentNetTotal = (typeof netSalary === 'number' && typeof dividend === 'number' && !isNaN(netSalary) && !isNaN(dividend))
        ? netSalary + dividend
        : 0;

      if (currentNetTotal !== previousNetTotal && previousNetTotal !== 0) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 600);
      }

      setPreviousNetTotal(currentNetTotal);
    }
  }, [netSalary, dividend, currentStep, showResult, previousNetTotal]);

  // Ensure users see the step header and description when step changes
  useEffect(() => {
    // Longer delay for step 4 to allow DOM to fully update with step content including revenue section
    const delay = currentStep === 4 ? 400 : 250;
    const scrollTimer = setTimeout(() => {
      scrollToStepHeader(currentStep);
    }, delay);

    return () => clearTimeout(scrollTimer);
  }, [currentStep]);

  // Styles
  const inputStyle = {
    padding: '12px 16px',
    margin: '5px 0',
    border: '2px solid #e1e5e9',
    borderRadius: '8px',
    fontSize: '1rem',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    transition: 'border-color 0.3s ease',
    display: 'block',
    alignSelf: 'stretch',
  };

  const buttonStyle = {
    backgroundColor: primaryColor,
    color: 'white',
    padding: '12px 24px',
    margin: '10px 5px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const ctaButtonStyle = {
    backgroundColor: complementaryColor,
    color: '#333',
    padding: '12px 24px',
    margin: '10px 5px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const inputContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
    gap: '8px',
    alignItems: 'stretch', // stretch för att label och input alltid ska ha samma vänsterstart
    width: '100%',
  };

  const labelStyle = {
    display: 'block',
    fontWeight: '500',
    color: '#333',
    fontSize: '1rem',
    textAlign: 'left',
    paddingLeft: 0,
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: 0,
  };

  const sliderContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
    maxWidth: '340px',
    minWidth: '220px',
    alignSelf: 'center',
  };

  const sliderValueStyle = {
    textAlign: 'center',
    fontSize: '1.1rem',
    fontWeight: '600',
    color: primaryColor,
    padding: '8px',
    background: '#f8f9fa',
    width: '100%',
    maxWidth: '340px',
    minWidth: '220px',
    boxSizing: 'border-box',
    alignSelf: 'center',
  };

  const iconStyle = {
    marginRight: '8px',
    fontSize: '1.2rem',
  };

  const containerStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: isMobile ? '24px' : isTablet ? '28px' : '32px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    maxWidth: 600,
    margin: isMobile ? '16px auto' : '24px auto',
    color: '#333',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    border: '1px solid #e1e5e9',
  };

  const resultStyle = {
    marginTop: isMobile ? '24px' : '32px',
    padding: isMobile ? '16px' : '20px',
    borderTop: '2px solid #e1e5e9',
    fontSize: '1.1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
  };

  const titleStyle = {
    textAlign: 'center',
    color: '#333',
    marginBottom: isMobile ? '24px' : '32px',
    fontSize: isMobile ? '1.6rem' : '1.8rem',
    fontWeight: '600',
  };

  // Progress bar styles
  const progressBarStyle = {
    width: '100%',
    height: isMobile ? '6px' : '8px',
    backgroundColor: '#e1e5e9',
    borderRadius: '4px',
    overflow: 'hidden',
    marginBottom: isMobile ? '24px' : '32px',
  };

  const progressFillStyle = {
    height: '100%',
    backgroundColor: primaryColor,
    width: `${(currentStep / totalSteps) * 100}%`,
    transition: 'width 0.3s ease',
    borderRadius: '4px',
  };

  // Step indicator styles
  const stepIndicatorStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: isMobile ? '24px' : '32px',
    padding: isMobile ? '0 10px' : '0 20px',
  };

  const stepCircleStyle = (step) => ({
    width: isMobile ? '32px' : '40px',
    height: isMobile ? '32px' : '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isMobile ? '12px' : '14px',
    fontWeight: '600',
    cursor: step <= currentStep ? 'pointer' : 'default',
    backgroundColor: isStepCompleted(step) ? primaryColor :
      step === currentStep ? complementaryColor : '#e1e5e9',
    color: isStepCompleted(step) ? 'white' :
      step === currentStep ? '#333' : '#999',
    transition: 'all 0.3s ease',
    border: 'none',
    flexShrink: 0,
  });

  const stepLineStyle = {
    flex: 1,
    height: '2px',
    backgroundColor: '#e1e5e9',
    margin: isMobile ? '0 5px' : '0 10px',
  };

  // Navigation button styles
  const navButtonStyle = {
    backgroundColor: primaryColor,
    color: 'white',
    padding: isMobile ? '10px 16px' : '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: isMobile ? '0.9rem' : '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    disabled: false,
    minWidth: isMobile ? '80px' : '120px',
  };

  const navButtonDisabledStyle = {
    ...navButtonStyle,
    backgroundColor: '#ccc',
    cursor: 'not-allowed',
    opacity: 0.6,
  };

  const prevButtonStyle = {
    backgroundColor: 'transparent',
    color: primaryColor,
    border: `2px solid ${primaryColor}`,
    padding: isMobile ? '10px 16px' : '12px 24px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: isMobile ? '0.9rem' : '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minWidth: isMobile ? '80px' : '120px',
  };

  const navigationContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: isMobile ? '24px' : '32px',
    gap: isMobile ? '12px' : '16px',
    flexWrap: isMobile ? 'wrap' : 'nowrap',
  };

  // Render step content
  const renderStepContent = () => {
    const inputGroupStyle = {
      marginBottom: isMobile ? '20px' : '24px',
    };

    const labelStyle = {
      display: 'block',
      fontWeight: '500',
      color: '#333',
      fontSize: isMobile ? '0.9rem' : '1rem',
      marginBottom: isMobile ? '8px' : '10px',
    };

    const inputStyle = {
      padding: isMobile ? '10px 14px' : '12px 16px',
      margin: isMobile ? '5px 0 8px 0' : '5px 0 10px 0',
      border: '2px solid #e1e5e9',
      borderRadius: '8px',
      fontSize: isMobile ? '0.9rem' : '1rem',
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      transition: 'border-color 0.3s ease',
      display: 'block',
      alignSelf: 'stretch',
    };

    const hintStyle = {
      fontSize: isMobile ? '12px' : '14px',
      color: '#666',
      marginTop: isMobile ? '6px' : '8px',
      marginBottom: isMobile ? '4px' : '6px',
      lineHeight: '1.4',
    };

    const sliderContainerStyle = {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '10px' : '12px',
      width: '100%',
      maxWidth: isMobile ? '100%' : isTablet ? '500px' : '600px',
      minWidth: isMobile ? '100%' : '220px',
      alignSelf: 'center',
      marginTop: isMobile ? '8px' : '10px',
      marginBottom: isMobile ? '8px' : '10px',
    };

    const sliderValueStyle = {
      textAlign: 'center',
      fontSize: isMobile ? '1rem' : '1.1rem',
      fontWeight: '600',
      color: primaryColor,
      padding: isMobile ? '6px' : '8px',
      background: '#f8f9fa',
      width: '100%',
      boxSizing: 'border-box',
      alignSelf: 'center',
      borderRadius: '6px',
    };

    switch (currentStep) {
      case 1:
        return (
          <div className="apple-calc-input-group" style={inputGroupStyle}>
            <label className="apple-calc-label" style={labelStyle}>
              💰 Fakturering (kr per timme)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9 ]*"
              value={hourlyRate ? hourlyRate.toLocaleString('sv-SE') : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '');
                setHourlyRate(raw === '' ? 0 : parseFloat(raw));
              }}
              className="apple-calc-input"
              style={inputStyle}
              placeholder="T.ex. 800"
              autoFocus
            />
            <div className="apple-calc-hint" style={hintStyle}>
              Ange din fakturering per timme exkl. moms.
            </div>

            {/* Enhanced Early Preview Section - Step 1 */}
            {hourlyRate > 0 && (
              <div className="apple-calc-early-preview" style={{
                marginTop: '20px',
                padding: isMobile ? '16px' : '20px',
                borderRadius: '12px',
                backgroundColor: 'linear-gradient(135deg, #e8f5e8 0%, #f0f9ff 100%)',
                border: '2px solid #22c55e',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)',
                animation: 'slideIn 0.5s ease-out',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}>
                  <span style={{ fontSize: isMobile ? '20px' : '24px' }}>⚡</span>
                  <strong style={{
                    fontSize: isMobile ? '1.1rem' : '1.3rem',
                    color: '#059669',
                    fontWeight: '700',
                  }}>
                    Snabbkalkyl baserat på din timtaxa!
                  </strong>
                </div>
                
                {/* Estimated Annual Revenue */}
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: '8px',
                  border: '1px solid #a7f3d0',
                }}>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#065f46', marginBottom: '4px' }}>
                    📊 Uppskattad årsomsättning
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.6rem' : '2rem',
                    fontWeight: '700',
                    color: '#059669',
                  }}>
                    {(hourlyRate * 40 * 47).toLocaleString('sv-SE')} kr
                  </div>
                  <div style={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#065f46',
                    marginTop: '4px',
                  }}>
                    {hourlyRate.toLocaleString('sv-SE')} kr/tim × 40 tim/vecka × 47 veckor
                  </div>
                </div>

                {/* Estimated Net Income */}
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '8px',
                  border: '1px solid #6ee7b7',
                }}>
                  <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#065f46', marginBottom: '4px' }}>
                    💰 Uppskattad nettoinkomst per år
                  </div>
                  <div style={{
                    fontSize: isMobile ? '1.4rem' : '1.6rem',
                    fontWeight: '700',
                    color: '#059669',
                  }}>
                    {Math.round(hourlyRate * 40 * 47 * 0.65).toLocaleString('sv-SE')} kr
                  </div>
                  <div style={{
                    fontSize: isMobile ? '11px' : '12px',
                    color: '#065f46',
                    marginTop: '4px',
                  }}>
                    Lön + utdelning efter skatt (ca 65% av omsättning)
                  </div>
                </div>

                {/* Action buttons for immediate next steps */}
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '12px',
                  justifyContent: 'center',
                  marginBottom: '12px',
                }}>
                  <button
                    onClick={nextStep}
                    style={{
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontSize: isMobile ? '14px' : '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#047857';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#059669';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    📋 Fortsätt för exakt beräkning
                  </button>
                  
                  {isMobile && (
                    <button
                      onClick={() => {
                        // Set reasonable defaults for quick results
                        setWorkWeeks(47);
                        setWorkHours(40);
                        setSelectedMunicipality('Stockholm');
                        setMunicipalityTaxRate(29.82);
                        setOtherCompanyCosts(50000);
                        goToStep(4);
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#059669',
                        border: '2px solid #059669',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f0fdf4';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                      }}
                    >
                      🚀 Hoppa till slutresultat
                    </button>
                  )}
                </div>

                <div style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  padding: '10px',
                  borderRadius: '8px',
                  fontSize: isMobile ? '12px' : '13px',
                  color: '#1e40af',
                  lineHeight: '1.4',
                }}>
                  <span style={{ fontSize: '14px' }}>💡</span> <strong>Fortsätt för exakt beräkning!</strong><br />
                  Dessa siffror baseras på standardvärden. Fortsätt till nästa steg för att anpassa efter dina förutsättningar och få en exakt beräkning.
                </div>

                <style jsx>{`
                  @keyframes slideIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                  }
                `}</style>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <>
            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                📅 Antal arbetsveckor per år
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={workWeeks === 0 ? '' : workWeeks}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setWorkWeeks(0);
                  } else {
                    const num = parseInt(val, 10);
                    if (!isNaN(num)) {
                      setWorkWeeks(num);
                    }
                  }
                }}
                onBlur={(e) => {
                  let num = parseInt(e.target.value, 10);
                  if (isNaN(num)) num = 0;
                  if (num < 1) num = 1;
                  if (num > 52) num = 52;
                  setWorkWeeks(num);
                }}
                className="apple-calc-input"
                style={inputStyle}
                min="1"
                max="52"
                placeholder="T.ex. 47"
                autoFocus
              />
              <div className="apple-calc-hint" style={hintStyle}>
                Normalt 44–48 veckor beroende på semester och ledighet.
              </div>
            </div>

            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                ⏰ Antal arbetstimmar per vecka
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={workHours === 0 ? '' : workHours}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setWorkHours(0);
                  } else {
                    const num = parseInt(val, 10);
                    if (!isNaN(num)) {
                      setWorkHours(num);
                    }
                  }
                }}
                onBlur={(e) => {
                  let num = parseInt(e.target.value, 10);
                  if (isNaN(num)) num = 0;
                  if (num < 1) num = 1;
                  if (num > 100) num = 100;
                  setWorkHours(num);
                }}
                className="apple-calc-input"
                style={inputStyle}
                min="1"
                max="100"
                placeholder="T.ex. 40"
              />
              <div className="apple-calc-hint" style={hintStyle}>
                Vanligtvis 36–40 timmar för heltid.
              </div>
            </div>
          </>
        );

      case 3:
        return (
          <>
            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                🏛️ Min kommun
              </label>
              <select
                value={selectedMunicipality}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setSelectedMunicipality(selectedValue);
                  const municipality = MUNICIPALITIES_DATA_UNSORTED.find(
                    (m) => m.value === selectedValue
                  );
                  setMunicipalityTaxRate(municipality ? municipality.rate : 33);
                }}
                className="apple-calc-input"
                style={inputStyle}
                autoFocus
              >
                <option value="">Välj kommun</option>
                {MUNICIPALITIES_DATA_UNSORTED
                  .sort((a, b) => a.label.localeCompare(b.label, 'sv-SE'))
                  .map((municipality) => (
                    <option key={municipality.value} value={municipality.value}>
                      {municipality.label} ({municipality.rate}%)
                    </option>
                  ))}
              </select>
              <div className="apple-calc-hint" style={hintStyle}>
                Välj din hemkommun för korrekt skattesats.
              </div>
            </div>

            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                💸 Företagets övriga kostnader per år (kr)
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9 ]*"
                value={otherCompanyCosts ? otherCompanyCosts.toLocaleString('sv-SE') : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '');
                  setOtherCompanyCosts(raw === '' ? 0 : parseFloat(raw));
                }}
                className="apple-calc-input"
                style={inputStyle}
                placeholder="T.ex. 50 000"
              />
              <div className="apple-calc-hint" style={hintStyle}>
                T.ex. försäkringar, bokföring, utrustning, resor m.m.
              </div>
            </div>

            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                📊 Privat skattesats (%)
              </label>
              <input
                type="number"
                value={totalIncomeTaxRate.toFixed(2)}
                readOnly
                disabled
                className="apple-calc-input"
                style={{
                  ...inputStyle,
                  backgroundColor: '#f8f9fa',
                  cursor: 'not-allowed',
                }}
                tabIndex={-1}
              />
              <div className="apple-calc-hint" style={hintStyle}>
                Summan av kommunal och eventuell statlig skatt baserat på vald bruttolön.
              </div>
            </div>
          </>
        );

      case 4:
        return (
          <>
            {/* Annual Revenue Display */}
            <div className="apple-calc-input-group" style={{
              ...inputGroupStyle,
              marginBottom: isMobile ? '24px' : '32px',
            }}>
              <div className="apple-calc-revenue-display" style={{
                padding: isMobile ? '20px' : '24px',
                backgroundColor: '#f0f9ff',
                borderRadius: '12px',
                border: '2px solid #0ea5e9',
                textAlign: 'center',
              }}>
                <h3 style={{
                  fontSize: isMobile ? '1.1rem' : '1.3rem',
                  fontWeight: '600',
                  color: '#0369a1',
                  marginBottom: isMobile ? '8px' : '12px',
                  marginTop: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}>
                  💰 Fakturering per år
                </h3>
                <div style={{
                  fontSize: isMobile ? '1.6rem' : '2rem',
                  fontWeight: '700',
                  color: '#0369a1',
                  marginBottom: isMobile ? '6px' : '8px',
                }}>
                  {(hourlyRate > 0 && workWeeks > 0 && workHours > 0
                    ? (hourlyRate * workWeeks * workHours).toLocaleString('sv-SE')
                    : '0')} kr
                </div>
                <div style={{
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: '#075985',
                  fontWeight: '500',
                }}>
                  {hourlyRate > 0 && workWeeks > 0 && workHours > 0 ? (
                    <>
                      {hourlyRate.toLocaleString('sv-SE')} kr/tim × {workWeeks} veckor × {workHours} tim/vecka
                    </>
                  ) : (
                    'Ange dina uppgifter i de tidigare stegen'
                  )}
                </div>
              </div>
            </div>

            <div className="apple-calc-input-group" style={inputGroupStyle}>
              <label className="apple-calc-label" style={labelStyle}>
                💼 Önskad bruttolön per månad
              </label>
              
              {/* Elegant instruktion för slidern */}
              {desiredGrossSalary === 0 && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  marginBottom: '12px',
                  textAlign: 'center',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  color: '#92400e',
                  fontWeight: '500',
                }}>
                  👆 Dra i slidern för att välja din bruttolön och se resultatet
                </div>
              )}

              <div
                className="apple-calc-slider-container"
                style={{
                  ...sliderContainerStyle,
                  touchAction: 'pan-y pinch-zoom', // Allow vertical scrolling and pinch zoom but not horizontal
                  overflow: 'hidden', // Prevent horizontal overflow
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div className="apple-calc-slider-value" style={{
                  ...sliderValueStyle,
                  backgroundColor: desiredGrossSalary > 0 ? '#f0fdf4' : '#f8f9fa',
                  border: desiredGrossSalary > 0 ? '2px solid #22c55e' : '2px solid #e5e7eb',
                  color: desiredGrossSalary > 0 ? '#059669' : primaryColor,
                  fontWeight: '700',
                  fontSize: isMobile ? '1.2rem' : '1.3rem',
                }}>
                  {typeof desiredGrossSalary === 'number' && !isNaN(desiredGrossSalary)
                    ? Math.round(desiredGrossSalary).toLocaleString('sv-SE')
                    : '0'} kr
                </div>
                <input
                  type="range"
                  min={0}
                  max={maxGrossSalary > 0 ? Math.floor(maxGrossSalary) : 0}
                  value={desiredGrossSalary > maxGrossSalary ? Math.floor(maxGrossSalary) : Math.round(desiredGrossSalary)}
                  onChange={(e) => setDesiredGrossSalary(Math.round(parseFloat(e.target.value)))}
                  className="apple-calc-slider"
                  style={{
                    width: '100%',
                    height: isMobile ? '8px' : '8px',
                    borderRadius: '4px',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    appearance: 'none',
                    background: '#e1e5e9',
                    touchAction: 'pan-x',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    cursor: 'pointer',
                  }}
                  disabled={maxGrossSalary === 0}
                  step={1}
                  // Prevent scrolling during slider interaction on mobile
                  onTouchStart={(e) => {
                    // Prevent page scrolling when touching the slider
                    e.stopPropagation();
                    // Ensure only horizontal movement is allowed
                    if (isMobile) {
                      document.body.style.overflow = 'hidden';
                    }
                  }}
                  onTouchEnd={(e) => {
                    // Re-enable page scrolling when done with slider
                    e.stopPropagation();
                    if (isMobile) {
                      document.body.style.overflow = '';
                    }
                  }}
                  onTouchMove={(e) => {
                    // Allow slider movement but prevent page scrolling
                    e.stopPropagation();
                  }}
                  // Also handle mouse events for consistency
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                />
                <div className="apple-calc-hint" style={{
                  ...hintStyle,
                  textAlign: 'center',
                  marginTop: '8px'
                }}>
                  Max möjlig: {typeof maxGrossSalary === 'number' && !isNaN(maxGrossSalary)
                    ? Math.round(maxGrossSalary).toLocaleString('sv-SE')
                    : '0'} kr
                </div>
              </div>
            </div>

            {/* Cost Warning */}
            {showCostWarning && (
              <div className="apple-calc-warning" style={{
                marginTop: isMobile ? '16px' : '20px',
                marginBottom: isMobile ? '16px' : '20px',
                padding: isMobile ? '16px' : '20px',
                backgroundColor: '#fff3cd',
                borderRadius: '12px',
                border: '2px solid #ffeaa7',
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.15)',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}>
                  <span style={{
                    fontSize: isMobile ? '1.2rem' : '1.4rem',
                    flexShrink: 0,
                  }}>
                    ⚠️
                  </span>
                  <div style={{
                    flex: 1,
                  }}>
                    <h4 style={{
                      fontSize: isMobile ? '0.95rem' : '1.05rem',
                      fontWeight: '600',
                      color: '#856404',
                      marginBottom: '8px',
                      marginTop: 0,
                    }}>
                      Företagskostnaderna är för höga
                    </h4>
                    <p style={{
                      fontSize: isMobile ? '0.85rem' : '0.95rem',
                      color: '#856404',
                      marginBottom: 0,
                      lineHeight: '1.4',
                    }}>
                      Du måste minska din bruttolön för att kunna hantera dessa kostnader.
                      De totala kostnaderna (lön + arbetsgivaravgift + företagskostnader)
                      överstiger dina intäkter.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Results Display */}
            {desiredGrossSalary > 0 && (
              <div className="apple-calc-results-preview" style={{
                marginTop: isMobile ? '24px' : '32px',
              }}>
                {/* Net Salary Section */}
                <div className="apple-calc-result-section" style={{
                  marginBottom: isMobile ? '20px' : '24px',
                  padding: isMobile ? '18px' : '22px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e1e5e9',
                }}>
                  <h3 className="apple-calc-title-3" style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: isMobile ? '14px' : '18px',
                    textAlign: 'center',
                  }}>
                    Nettolön per år
                  </h3>
                  <div className="apple-calc-result-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <span className="apple-calc-result-label" style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: '500',
                      color: '#333',
                    }}>
                      Lön efter skatt:
                    </span>
                    <span className="apple-calc-result-value-primary" style={{
                      fontSize: isMobile ? '1.1rem' : '1.2rem',
                      fontWeight: '700',
                      color: primaryColor,
                    }}>
                      {typeof netSalary === 'number' && !isNaN(netSalary)
                        ? Math.round(netSalary).toLocaleString('sv-SE')
                        : '0'} kr
                    </span>
                  </div>

                  {/* Tax breakdown */}
                  <div style={{
                    borderTop: '1px solid #e1e5e9',
                    paddingTop: isMobile ? '10px' : '12px',
                    marginTop: isMobile ? '10px' : '12px',
                  }}>
                    <div className="apple-calc-result-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: isMobile ? '6px' : '8px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                    }}>
                      <span>Kommunal skatt:</span>
                      <span>{Math.round(municipalTaxAmount).toLocaleString('sv-SE')} kr</span>
                    </div>
                    <div className="apple-calc-result-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: isMobile ? '6px' : '8px',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                    }}>
                      <span>Statlig skatt:</span>
                      <span>{Math.round(stateTaxAmount).toLocaleString('sv-SE')} kr</span>
                    </div>
                    <div className="apple-calc-result-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                    }}>
                      <span>Arbetsgivaravgift:</span>
                      <span>{Math.round(employerFeeAmount).toLocaleString('sv-SE')} kr</span>
                    </div>
                  </div>
                </div>

                {/* Dividend Section */}
                <div className="apple-calc-result-section" style={{
                  marginBottom: isMobile ? '20px' : '24px',
                  padding: isMobile ? '18px' : '22px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e1e5e9',
                }}>
                  <h3 className="apple-calc-title-3" style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: isMobile ? '14px' : '18px',
                    textAlign: 'center',
                  }}>
                    Möjlig utdelning
                  </h3>
                  <div className="apple-calc-result-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <span className="apple-calc-result-label" style={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: '500',
                      color: '#333',
                    }}>
                      Utdelning efter skatt:
                    </span>
                    <span className="apple-calc-result-value-primary" style={{
                      fontSize: isMobile ? '1.1rem' : '1.2rem',
                      fontWeight: '700',
                      color: primaryColor,
                    }}>
                      {typeof dividend === 'number' && !isNaN(dividend)
                        ? Math.round(dividend).toLocaleString('sv-SE')
                        : '0'} kr
                    </span>
                  </div>
                  <div style={{
                    borderTop: '1px solid #e1e5e9',
                    paddingTop: '8px',
                    marginTop: '8px',
                  }}>
                    <div className="apple-calc-result-row" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#666',
                    }}>
                      <span>Skatt på utdelning:</span>
                      <span>{Math.round(dividendTaxAmount).toLocaleString('sv-SE')} kr</span>
                    </div>
                  </div>
                </div>

                {/* Total Section */}
                <div className="apple-calc-result-section" style={{
                  marginBottom: isMobile ? '24px' : '28px',
                  padding: isMobile ? '20px' : '24px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '12px',
                  border: `2px solid ${primaryColor}`,
                }}>
                  <h3 className="apple-calc-title-3" style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: isMobile ? '16px' : '20px',
                    textAlign: 'center',
                  }}>
                    Summa netto per år
                  </h3>
                  <div className="apple-calc-result-row" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <span className="apple-calc-result-label" style={{
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      fontWeight: '600',
                      color: '#333',
                    }}>
                      Totalt efter skatt:
                    </span>
                    <span className="apple-calc-result-value-primary" style={{
                      fontSize: isMobile ? '1.4rem' : '1.6rem',
                      fontWeight: '700',
                      color: primaryColor,
                    }}>
                      {typeof netSalary === 'number' && typeof dividend === 'number' && !isNaN(netSalary) && !isNaN(dividend)
                        ? Math.round(netSalary + dividend).toLocaleString('sv-SE')
                        : '0'} kr
                    </span>
                  </div>
                </div>

                {/* Experiment with Values Section - moved here */}
                <div className="apple-calc-input-summary" style={{
                  marginBottom: isMobile ? '20px' : '24px',
                  padding: isMobile ? '22px' : '26px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '16px',
                  border: '2px solid #e1e5e9',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                }}>
                  <h3 style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: isMobile ? '18px' : '22px',
                    textAlign: 'center',
                  }}>
                    🔄 Experimentera med olika värden
                  </h3>
                  <p style={{
                    fontSize: isMobile ? '14px' : '15px',
                    color: '#666',
                    textAlign: 'center',
                    marginBottom: isMobile ? '18px' : '20px',
                    lineHeight: '1.5',
                  }}>
                    Ändra värdena nedan för att se hur det påverkar din lön och utdelning i realtid
                  </p>

                  {/* Current Gross Salary Display */}
                  <div style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: isMobile ? '16px' : '18px',
                    marginBottom: isMobile ? '20px' : '24px',
                    border: '2px solid #e1e5e9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                    gap: isMobile ? '12px' : '16px',
                  }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      flex: 1,
                      minWidth: isMobile ? '100%' : 'auto',
                    }}>
                      <span style={{
                        fontSize: isMobile ? '12px' : '13px',
                        color: '#888',
                        fontWeight: '500',
                      }}>
                        💼 Aktuell bruttolön/månad
                      </span>
                      <span style={{
                        fontSize: isMobile ? '1.1rem' : '1.2rem',
                        fontWeight: '700',
                        color: primaryColor,
                      }}>
                        {typeof desiredGrossSalary === 'number' && !isNaN(desiredGrossSalary)
                          ? Math.round(desiredGrossSalary).toLocaleString('sv-SE')
                          : '0'} kr
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        // Scroll to the top of the calculator card where the slider is
                        const calculatorCard = document.querySelector('.apple-calc-card');
                        if (calculatorCard) {
                          calculatorCard.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest'
                          });
                        }
                      }}
                      style={{
                        backgroundColor: primaryColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: isMobile ? '8px 16px' : '10px 20px',
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        whiteSpace: 'nowrap',
                        minWidth: isMobile ? '100%' : 'auto',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e04444';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = primaryColor;
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      ✏️ Ändra bruttolön
                    </button>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: isMobile ? '18px' : '22px',
                    marginBottom: isMobile ? '18px' : '22px',
                  }}>
                    {/* Hourly Rate */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#555',
                        fontWeight: '600',
                      }}>
                        💰 Timtaxa (kr)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9 ]*"
                        value={hourlyRate ? hourlyRate.toLocaleString('sv-SE') : ''}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '');
                          setHourlyRate(raw === '' ? 0 : parseFloat(raw));
                        }}
                        style={{
                          padding: isMobile ? '10px 12px' : '12px 14px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: isMobile ? '14px' : '15px',
                          textAlign: 'center',
                          fontWeight: '500',
                          transition: 'border-color 0.3s ease',
                          backgroundColor: 'white',
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        placeholder="800"
                      />
                    </div>

                    {/* Work Weeks */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#555',
                        fontWeight: '600',
                      }}>
                        📅 Arbetsveckor/år
                      </label>
                      <input
                        type="number"
                        value={workWeeks === 0 ? '' : workWeeks}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setWorkWeeks(0);
                          } else {
                            const num = parseInt(val, 10);
                            if (!isNaN(num) && num >= 0 && num <= 52) {
                              setWorkWeeks(num);
                            }
                          }
                        }}
                        style={{
                          padding: isMobile ? '10px 12px' : '12px 14px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: isMobile ? '14px' : '15px',
                          textAlign: 'center',
                          fontWeight: '500',
                          transition: 'border-color 0.3s ease',
                          backgroundColor: 'white',
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        min="1"
                        max="52"
                        placeholder="47"
                      />
                    </div>

                    {/* Work Hours */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#555',
                        fontWeight: '600',
                      }}>
                        ⏰ Timmar/vecka
                      </label>
                      <input
                        type="number"
                        value={workHours === 0 ? '' : workHours}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '') {
                            setWorkHours(0);
                          } else {
                            const num = parseInt(val, 10);
                            if (!isNaN(num) && num >= 0 && num <= 100) {
                              setWorkHours(num);
                            }
                          }
                        }}
                        style={{
                          padding: isMobile ? '10px 12px' : '12px 14px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: isMobile ? '14px' : '15px',
                          textAlign: 'center',
                          fontWeight: '500',
                          transition: 'border-color 0.3s ease',
                          backgroundColor: 'white',
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        min="1"
                        max="100"
                        placeholder="40"
                      />
                    </div>

                    {/* Municipality */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#555',
                        fontWeight: '600',
                      }}>
                        🏛️ Kommun
                      </label>
                      <select
                        value={selectedMunicipality}
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          setSelectedMunicipality(selectedValue);
                          const municipality = MUNICIPALITIES_DATA_UNSORTED.find(
                            (m) => m.value === selectedValue
                          );
                          setMunicipalityTaxRate(municipality ? municipality.rate : 33);
                        }}
                        style={{
                          padding: isMobile ? '10px 12px' : '12px 14px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: isMobile ? '13px' : '14px',
                          textAlign: 'center',
                          fontWeight: '500',
                          appearance: 'none',
                          backgroundImage: "url(\"data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>\")",
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '12px',
                          paddingRight: '32px',
                          backgroundColor: 'white',
                          transition: 'border-color 0.3s ease',
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                      >
                        <option value="">Välj kommun</option>
                        {MUNICIPALITIES_DATA_UNSORTED
                          .sort((a, b) => a.label.localeCompare(b.label, 'sv-SE'))
                          .map((municipality) => (
                            <option key={municipality.value} value={municipality.value}>
                              {municipality.label} ({municipality.rate}%)
                            </option>
                          ))}
                      </select>
                      {/* Total Tax Rate Display */}
                      <div style={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: '#666',
                        textAlign: 'center',
                        marginTop: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#f0f8ff',
                        borderRadius: '6px',
                        border: '1px solid #e1e8f0',
                      }}>
                        📊 Total skattesats: {totalIncomeTaxRate.toFixed(1)}%
                        <div style={{
                          fontSize: isMobile ? '10px' : '11px',
                          color: '#888',
                          marginTop: '2px',
                        }}>
                          (Kommunal + statlig skatt)
                        </div>
                      </div>
                    </div>

                    {/* Company Costs - Full width */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      gridColumn: isMobile ? '1' : 'span 2',
                    }}>
                      <label style={{
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#555',
                        fontWeight: '600',
                      }}>
                        💸 Företagskostnader/år (kr)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9 ]*"
                        value={otherCompanyCosts ? otherCompanyCosts.toLocaleString('sv-SE') : ''}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '');
                          setOtherCompanyCosts(raw === '' ? 0 : parseFloat(raw));
                        }}
                        style={{
                          padding: isMobile ? '10px 12px' : '12px 14px',
                          border: '2px solid #ddd',
                          borderRadius: '8px',
                          fontSize: isMobile ? '14px' : '15px',
                          textAlign: 'center',
                          fontWeight: '500',
                          transition: 'border-color 0.3s ease',
                          backgroundColor: 'white',
                        }}
                        onFocus={(e) => e.target.style.borderColor = primaryColor}
                        onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        placeholder="50 000"
                      />
                    </div>
                  </div>

                  <div style={{
                    padding: isMobile ? '12px 16px' : '14px 20px',
                    backgroundColor: '#e8f4fd',
                    borderRadius: '10px',
                    fontSize: isMobile ? '13px' : '14px',
                    color: '#555',
                    textAlign: 'center',
                    lineHeight: '1.5',
                    border: '1px solid #d1ecf1',
                  }}>
                    <span style={{ fontSize: '16px' }}>💡</span> Prova olika scenarier genom att ändra värdena ovan - resultatet uppdateras automatiskt!
                  </div>

                  {/* Gamified Net Result Display */}
                  <div style={{
                    marginTop: isMobile ? '20px' : '24px',
                    padding: isMobile ? '20px' : '24px',
                    backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    background: isAnimating
                      ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '16px',
                    textAlign: 'center',
                    color: 'white',
                    boxShadow: isAnimating
                      ? '0 8px 32px rgba(240, 147, 251, 0.4), 0 0 0 2px rgba(245, 87, 108, 0.3)'
                      : '0 6px 20px rgba(102, 126, 234, 0.3)',
                    transform: isAnimating ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}>
                    {/* Sparkle effect background */}
                    {isAnimating && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.8) 1px, transparent 1px), radial-gradient(circle at 70% 80%, rgba(255,255,255,0.6) 1px, transparent 1px), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.4) 1px, transparent 1px)',
                        animation: 'sparkle 0.6s ease-out',
                        pointerEvents: 'none',
                      }} />
                    )}

                    <div style={{
                      position: 'relative',
                      zIndex: 2,
                    }}>
                      <div style={{
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        marginBottom: '8px',
                        opacity: '0.9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}>
                        <span style={{ fontSize: isMobile ? '18px' : '20px' }}>
                          {isAnimating ? '🎉' : '💰'}
                        </span>
                        Summa netto per år
                        {isAnimating && (
                          <span style={{
                            fontSize: isMobile ? '18px' : '20px',
                            animation: 'bounce 0.6s ease-out',
                          }}>
                            ✨
                          </span>
                        )}
                      </div>
                      <div style={{
                        fontSize: isMobile ? '2rem' : '2.5rem',
                        fontWeight: '800',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transform: isAnimating ? 'scale(1.1)' : 'scale(1)',
                        transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}>
                        {typeof netSalary === 'number' && typeof dividend === 'number' && !isNaN(netSalary) && !isNaN(dividend)
                          ? Math.round(netSalary + dividend).toLocaleString('sv-SE')
                          : '0'} kr
                      </div>
                      <div style={{
                        fontSize: isMobile ? '12px' : '13px',
                        opacity: '0.8',
                        marginTop: '4px',
                      }}>
                        Nettolön + utdelning efter skatt
                      </div>
                    </div>

                    {/* Add CSS keyframes for animations */}
                    <style jsx>{`
                      @keyframes sparkle {
                        0%, 100% { opacity: 0; transform: scale(0.8); }
                        50% { opacity: 1; transform: scale(1.2); }
                      }
                      @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-4px); }
                      }
                    `}</style>
                  </div>
                </div>

                {/* Conversion Section - NEW */}
                <div style={{
                  marginTop: '24px',
                  marginBottom: '24px',
                  padding: '24px',
                  backgroundColor: '#fff',
                  border: `2px solid ${theme.primaryColor}`,
                  borderRadius: '16px',
                  textAlign: 'center',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '1.2rem', color: '#333' }}>
                    Gör kalkylen till verklighet
                  </h3>

                  <p style={{ marginBottom: '20px', color: '#555' }}>
                    Vi hjälper konsulter att hitta uppdrag med snittarvoden på
                    <strong> {hourlyRate} kr/h</strong>.
                  </p>

                  <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center' }}>
                    {/* Primary CTA */}
                    <AppPrimaryButton
                      href={JOBS_DASHBOARD_PATH}
                      style={{ width: isMobile ? '100%' : 'auto' }}
                    >
                      Se lediga uppdrag
                    </AppPrimaryButton>

                    {/* Secondary CTA */}
                    <AppOutlineButton
                      href={REGISTRATION_PATH}
                      style={{ width: isMobile ? '100%' : 'auto' }}
                      color={theme.primaryColor}
                    >
                      Skapa profil & bli headhuntad
                    </AppOutlineButton>
                  </div>
                </div>

                {/* Social Share Section */}
                <div className="apple-calc-result-section" style={{
                  marginBottom: isMobile ? '20px' : '24px',
                  padding: isMobile ? '18px' : '22px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}>
                  <h3 className="apple-calc-title-3" style={{
                    fontSize: isMobile ? '1rem' : '1.1rem',
                    marginBottom: isMobile ? '10px' : '12px',
                    color: '#333',
                  }}>
                    💬 Hjälp en vän med löneberäkningen
                  </h3>
                  <p style={{
                    textAlign: 'center',
                    fontSize: isMobile ? '13px' : '14px',
                    color: '#555',
                    margin: '0 0 16px 0',
                    lineHeight: '1.5',
                  }}>
                    Känner du någon som behöver räkna ut sin lön och utdelning? Skicka denna kalkylator så kan de få samma hjälp som du!
                  </p>
                  <BlogShareButton
                    path={location.pathname}
                    subject={'Hjälpsam lönekalkylator för aktiebolag'}
                    body={'Hej! Kolla in denna lönekalkylatorn för aktiebolag - super användbar för att räkna ut lön och utdelning'}
                  />
                </div>




                {/* Info Box */}
                <div className="apple-calc-info" style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  padding: isMobile ? '12px' : '16px',
                  fontSize: isMobile ? '12px' : '14px',
                  lineHeight: '1.5',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}>
                    <span className="apple-calc-info-icon" style={{
                      fontSize: isMobile ? '16px' : '18px',
                      flexShrink: 0,
                    }}>
                      ℹ️
                    </span>
                    <div className="apple-calc-info-content">
                      <strong>Denna kalkyl gäller aktiebolag.</strong><br />
                      Kalkylen tar hänsyn till:
                      <ul className="apple-calc-info-list" style={{
                        marginLeft: isMobile ? '16px' : '20px',
                        marginTop: '8px',
                        marginBottom: '8px',
                        paddingLeft: 0,
                      }}>
                        <li><strong>Bolagsskatt</strong> (20,6%) på företagets vinst</li>
                        <li><strong>Arbetsgivaravgift</strong> (31,42% på lön)</li>
                        <li><strong>Kommunalskatt</strong> (enligt vald kommun)</li>
                        <li><strong>Statlig inkomstskatt</strong> (20% på lön över 598 500 kr)</li>
                        <li><strong>Kapitalskatt på utdelning</strong> (20–30% enligt 3:12-reglerna)</li>
                        <li><strong>Företagets övriga kostnader</strong></li>
                      </ul>
                      <strong>Obs!</strong> Arbetsgivaravgift ingår i kalkylen. Tjänstepension ingår inte.<br />
                      Resultatet är en förenklad uppskattning för aktiebolag.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <>

      {/* Main Calculator Container */}
      <main
        className="salary-calculator-main"
        role="main"
        aria-label="Aktiebolag lön och utdelning kalkylator"
        itemScope
        itemType="https://schema.org/WebApplication"
      >
        {/* SEO Content Header */}
        <header className="calculator-header" style={{ textAlign: 'center', marginBottom: isMobile ? '8px' : '4px' }}>

          {/* SEO Keywords in hidden but accessible content */}
          <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
            <span>Räkna lön från fakturering, lön bemanning, fakturering till lön, räkna lön och utdelning, konsult lön kalkylator, aktiebolag utdelning, nettolön beräkning, skatteberäkning konsult</span>
          </div>
        </header>

        <div className="apple-calc-grid" style={{
          width: '100%',
          maxWidth: 'none',
          padding: isMobile ? '12px' : isTablet ? '24px' : '0',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}>
          {/* Main Calculator Card */}
          <article
            className="apple-calc-card"
            role="application"
            aria-label="Interaktiv kalkylator för lön och utdelning"
            itemScope
            itemType="https://schema.org/Calculator"
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              padding: isMobile ? '24px' : isTablet ? '28px' : '32px',
              backgroundColor: '#ffffff',
              borderRadius: isMobile ? '12px' : '16px',
              maxWidth: isMobile ? '100%' : isTablet ? '600px' : '700px',
              margin: isMobile ? '12px auto' : '0 0',
              color: '#333',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid #e1e5e9',
              width: '100%',
              boxSizing: 'border-box',
            }}>
            {/* Progress Bar */}
            <div style={progressBarStyle}>
              <div style={progressFillStyle}></div>
            </div>

            {/* Step Indicators */}
            <div style={stepIndicatorStyle}>
              {[1, 2, 3, 4].map((step, index) => (
                <React.Fragment key={step}>
                  <div
                    style={stepCircleStyle(step)}
                    onClick={() => step <= currentStep && goToStep(step)}
                    title={getStepTitle(step)}
                  >
                    {isStepCompleted(step) ? '✓' : step}
                  </div>
                  {index < 3 && <div style={stepLineStyle}></div>}
                </React.Fragment>
              ))}
            </div>

            {/* Step Header */}
            <div className="apple-calc-card-header" style={{
              textAlign: 'center',
              marginBottom: isMobile ? '24px' : '32px',
            }}>
              <h2 className="apple-calc-title-2" style={{
                textAlign: 'center',
                color: '#333',
                marginBottom: isMobile ? '10px' : '16px',
                fontSize: isMobile ? '1.4rem' : isTablet ? '1.6rem' : '1.8rem',
                fontWeight: '600',
              }}>
                {getStepTitle(currentStep)}
              </h2>
              <p style={{
                textAlign: 'center',
                color: '#666',
                margin: isMobile ? '10px 0 0 0' : '12px 0 0 0',
                fontSize: isMobile ? '14px' : '16px',
              }}>
                {getStepDescription(currentStep)}
              </p>
            </div>

            {/* Step Content */}
            <div className="apple-calc-step-content" style={{
              width: '100%',
              maxWidth: '100%',
              marginTop: isMobile ? '8px' : '12px',
              marginBottom: isMobile ? '8px' : '12px',
            }}>
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div style={{
              ...navigationContainerStyle,
              justifyContent: currentStep === 4 ? 'center' : 'space-between'
            }}>
              {currentStep < 4 && (
                <button
                  style={currentStep === 1 ? { ...prevButtonStyle, opacity: 0.5, cursor: 'not-allowed' } : prevButtonStyle}
                  onClick={prevStep}
                  disabled={currentStep === 1}
                >
                  {isMobile ? '← Föreg.' : '← Föregående'}
                </button>
              )}

              {currentStep < 4 && (
                <div style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#666',
                  textAlign: 'center',
                  minWidth: isMobile ? '60px' : '100px',
                  order: isMobile ? 3 : 0,
                  width: isMobile ? '100%' : 'auto',
                  marginTop: isMobile ? '10px' : '0',
                }}>
                  Steg {currentStep} av {totalSteps}
                </div>
              )}

              <button
                style={!validateCurrentStep() && currentStep < totalSteps ? navButtonDisabledStyle : navButtonStyle}
                onClick={currentStep === totalSteps ? () => goToStep(1) : nextStep}
                disabled={!validateCurrentStep() && currentStep < totalSteps}
              >
                {currentStep === totalSteps ? (isMobile ? '🔄 Gör om' : '🔄 Gör om') : (isMobile ? 'Nästa →' : 'Nästa →')}
              </button>
            </div>
          </article>
        </div>

        {/* SEO Footer Content */}
        <footer className="calculator-footer" style={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          maxWidth: '800px',
          margin: '40px auto 0 auto'
        }}>

          <section aria-labelledby="target-users" style={{ marginTop: '30px' }}>
            <h2 id="target-users" style={{
              fontSize: isMobile ? '1.2rem' : '1.4rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '15px'
            }}>
            </h2>
            <p style={{
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.6',
              maxWidth: '700px',
              margin: '0 auto'
            }}>
            </p>
          </section>
        </footer>
      </main>

      {/* Sticky CTA - Show after 5s on steps 1-3, immediately on step 4 */}
      <StickyCTA
        icon="🚀"
        title="Skapa konto och bli headhuntad"
        subtitle={hourlyRate > 0 ? `Hitta uppdrag med ${hourlyRate} kr/h` : "Hitta uppdrag med din lön"}
        href={REGISTRATION_PATH}
        showIcon={true}
        timeThreshold={currentStep === 4 ? 0 : 5000}
        scrollThreshold={currentStep === 4 ? 0 : 0.25}
      />
    </>
  );
};

export default SalaryCalculator;
